import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck } from 'lucide-react';
import {
  VENDOR_TIMES_SECTION_NAME,
  formatPlanStartTime,
  isVendorTimesSection,
  vendorTimeKindLabel,
  type AddTimelineVendorTimesResult,
  type TimelineVendorTime,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useAddTimelineVendorTimes, useTimelineVendorTimes } from '../../hooks/useEventOps';
import { extractApiErrorMessage } from '../../utils/apiError';

/** The picker's value for "a Vendors section, made for these". */
export const NEW_VENDORS_SECTION = '__new_vendors_section__';

const timeKey = (t: Pick<TimelineVendorTime, 'linkId' | 'kind'>) => `${t.linkId}|${t.kind}`;

export interface VendorTimesModalProps {
  open: boolean;
  onClose: () => void;
  timelineId: string;
  /** The event the timeline is for — its Vendors tab is where times are set. */
  eventId: string;
  /** The timeline's sections, to pick where the entries go. */
  sections: readonly { id: string; name: string }[];
  /** After entries were written — the page may open the section they went to. */
  onAdded?: (result: AddTimelineVendorTimesResult) => void;
}

/**
 * The event's booked vendors' arrival and load-in times, offered to the
 * timeline — ticked, except those already on it, which are shown ticked and
 * fixed ("On the timeline"). The times themselves are read from the bookings
 * by the server; only which ones and where are sent.
 */
export function VendorTimesModal({ open, ...rest }: VendorTimesModalProps) {
  return (
    <EntityModal open={open} onClose={rest.onClose} title="Vendor times" size="sm">
      {open && <VendorTimesBody {...rest} />}
    </EntityModal>
  );
}

function VendorTimesBody({
  onClose,
  timelineId,
  eventId,
  sections,
  onAdded,
}: Omit<VendorTimesModalProps, 'open'>) {
  const { data, isLoading, isError, refetch } = useTimelineVendorTimes(timelineId);
  const add = useAddTimelineVendorTimes();
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const vendorsSection = sections.find((s) => isVendorTimesSection(s.name));
  const sectionItems = useMemo(
    () => [
      ...(vendorsSection
        ? []
        : [{ id: NEW_VENDORS_SECTION, name: `${VENDOR_TIMES_SECTION_NAME} (new)` }]),
      ...sections,
    ],
    [sections, vendorsSection],
  );
  const [sectionId, setSectionId] = useState(vendorsSection?.id ?? NEW_VENDORS_SECTION);

  // Everything not on the timeline yet is ticked, once, when the times arrive —
  // a refetch never puts back a tick taken off.
  useEffect(() => {
    if (data && chosen === null) {
      setChosen(new Set(data.times.filter((t) => !t.onTimeline).map(timeKey)));
    }
  }, [data, chosen]);

  const times = data?.times ?? [];
  const picked = times.filter((t) => !t.onTimeline && chosen?.has(timeKey(t)));
  const count = picked.length;

  const toggle = (key: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const submit = async () => {
    setError(null);
    try {
      const result = await add.mutateAsync({
        timelineId,
        items: picked.map((t) => ({ linkId: t.linkId, kind: t.kind })),
        ...(sectionId !== NEW_VENDORS_SECTION ? { sectionId } : {}),
      });
      onAdded?.(result);
      onClose();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'The times could not be added. Try again.'));
    }
  };

  if (isLoading) {
    return (
      <p className="text-sm text-gray-500" role="status">
        Loading the vendors’ times…
      </p>
    );
  }
  if (isError) {
    return (
      <div role="alert" className="space-y-2 text-sm text-red-700">
        <p>The vendors’ times could not be loaded.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="min-h-11 sm:min-h-9 px-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Try again
        </button>
      </div>
    );
  }
  if (times.length === 0) {
    return (
      <div className="text-center py-6 space-y-2" data-testid="vendor-times-empty">
        <Truck className="mx-auto h-10 w-10 text-gray-300" aria-hidden="true" />
        <p className="text-sm text-gray-600">
          No booked vendor has an arrival or load-in time yet.
        </p>
        <Link
          to={`/events/${eventId}/vendors?view=vendors`}
          className="inline-flex items-center min-h-11 sm:min-h-0 text-sm text-primary-600 hover:text-primary-700"
        >
          Set arrival and load-in times on the Vendors tab
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        The arrival and load-in times set on the event’s vendors. Each one ticked goes on the
        timeline as an entry you can edit.
      </p>
      {error && (
        <p role="alert" className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">
          {error}
        </p>
      )}
      <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg max-h-80 overflow-auto">
        {times.map((t) => {
          const key = timeKey(t);
          return (
            <li key={key}>
              <label
                className={`flex items-center gap-3 px-3 min-h-11 py-2 ${
                  t.onTimeline ? 'cursor-default' : 'cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  checked={t.onTimeline || !!chosen?.has(key)}
                  disabled={t.onTimeline}
                  onChange={() => toggle(key)}
                  aria-label={`${t.vendorName} ${vendorTimeKindLabel(t.kind).toLowerCase()} ${formatPlanStartTime(t.time)}`}
                />
                <span className="w-20 shrink-0 text-sm font-medium text-gray-900 tabular-nums">
                  {formatPlanStartTime(t.time)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-gray-900 truncate">{t.vendorName}</span>
                  <span className="block text-xs text-gray-500">
                    {vendorTimeKindLabel(t.kind)}
                    {t.onTimeline && ' · On the timeline'}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      <div>
        <label
          htmlFor="vendor-times-section"
          className="block text-xs font-medium text-gray-500 mb-1"
        >
          Add to section
        </label>
        <SearchableListSelect
          inputId="vendor-times-section"
          items={sectionItems}
          value={sectionId}
          onChange={(v) => setSectionId(typeof v === 'string' && v ? v : NEW_VENDORS_SECTION)}
          getValue={(s) => s.id}
          getLabel={(s) => s.name}
          clearable={false}
          ariaLabel="Section"
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 sm:min-h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={count === 0 || add.isPending}
          className="min-h-11 sm:min-h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {add.isPending
            ? 'Adding…'
            : count === 0
              ? 'Nothing to add'
              : `Add ${count} to the timeline`}
        </button>
      </div>
    </div>
  );
}
